import { useState } from "react";
import { Lock, X, Loader2, FileDown } from "lucide-react";

export default function ProtectedDownload({ file, label, testId, className = "" }) {
    const [open, setOpen] = useState(false);
    const [pw, setPw] = useState("");
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);

    const fetchFile = async (password) => {
        setBusy(true);
        setError(false);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/${file}?pw=${encodeURIComponent(password)}`);
            if (!res.ok) {
                setError(true);
                return false;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = file;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            sessionStorage.setItem("lsf_forms_pw", password);
            setOpen(false);
            return true;
        } catch (e) {
            setError(true);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const click = async () => {
        const saved = sessionStorage.getItem("lsf_forms_pw");
        if (saved && (await fetchFile(saved))) return;
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        await fetchFile(pw);
    };

    return (
        <>
            <button
                type="button"
                onClick={click}
                data-testid={testId}
                className={`inline-flex items-center gap-2 bg-sage hover:bg-sagedeep text-white font-bold text-sm px-4 py-2.5 rounded-full transition-colors shrink-0 ${className}`}
            >
                <Lock size={15} /> {label}
            </button>

            {open && (
                <div className="fixed inset-0 z-[90] bg-soil/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="forms-password-modal">
                    <div className="bg-cream rounded-[2rem] border-2 border-soil/10 shadow-2xl max-w-md w-full p-7 md:p-8 relative">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            data-testid="forms-password-close"
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-soil/10 transition-colors"
                        >
                            <X size={18} />
                        </button>
                        <span className="inline-flex p-3 rounded-2xl bg-sun/25 mb-4">
                            <Lock size={22} className="text-soil" />
                        </span>
                        <h3 className="font-display font-semibold text-2xl mb-2">Password protected</h3>
                        <p className="text-soilmuted text-sm leading-relaxed mb-5">
                            Our forms include private family information, so downloads are just for touring and enrolled
                            families. Asia shares the password after your tour — lost it? Call or text 516-324-3418.
                        </p>
                        <form onSubmit={submit}>
                            <input
                                type="password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                placeholder="Enter the family password"
                                data-testid="forms-password-input"
                                autoFocus
                                className="w-full bg-white border-2 border-soil/15 rounded-2xl px-4 py-3 text-soil focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/25"
                            />
                            {error && (
                                <p className="text-coraldeep text-xs font-bold mt-2" data-testid="forms-password-error">
                                    That password doesn't match — try again, or text Asia at 516-324-3418.
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="forms-password-submit"
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-sun hover:bg-suncdeep disabled:opacity-60 text-soil font-extrabold px-6 py-3.5 rounded-full transition-all"
                            >
                                {busy ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />}
                                {busy ? "Preparing…" : "Unlock & download"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
